var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { MeetingDurationType } from '@web/components/Filters/Elements/MeetingDurationType';
import { MeetingParticipantsType } from '@web/components/Filters/Elements/MeetingParticipantsType';
import { MeetingType } from '@web/components/Filters/Elements/MeetingType';
import { getChartPeriodLabelForStack, navigateChartPeriodBarClick, navigateToChartDestination, navigateToChartDestinationFromPeriod, resolveChartBarPeriodIndex } from '@web/utility/chartNavigation';
import { ComparisonPeriod } from '@web/utility/comparisonPeriod';
import { useFilters } from '@web/utility/hooks/useFilters';
import { useNavigateTo } from '@web/utility/hooks/useNavigateTo';
import { MEETINGS_PLANNED_VS_ACTUAL_THRESHOLD_SECONDS } from '@workalizer/shared';
import { OVERRUN_MINUTES } from '@workalizer/shared/config';
import { useCallback } from 'react';
export { ComparisonPeriod };
export { getChartPeriodLabelForStack, resolveChartBarPeriodIndex };
var PLANNED_VS_ACTUAL_THRESHOLD_MINUTES = Math.round(MEETINGS_PLANNED_VS_ACTUAL_THRESHOLD_SECONDS / 60);
function toSearchMeetingsDestination(filters) {
    var _a, _b, _c, _d, _e, _f, _g;
    if (filters === void 0) { filters = {}; }
    return {
        kind: 'searchMeetings',
        meetingType: (_a = filters.meetingType) !== null && _a !== void 0 ? _a : null,
        meetingDurationType: (_b = filters.meetingDurationType) !== null && _b !== void 0 ? _b : null,
        durationHours: (_c = filters.durationHours) !== null && _c !== void 0 ? _c : null,
        durationMinutes: (_d = filters.durationMinutes) !== null && _d !== void 0 ? _d : null,
        meetingParticipantsType: (_e = filters.meetingParticipantsType) !== null && _e !== void 0 ? _e : null,
        title: (_f = filters.title) !== null && _f !== void 0 ? _f : null,
        activityLabelId: (_g = filters.activityLabelId) !== null && _g !== void 0 ? _g : null
    };
}
export function navigateToSearchMeetingsWithFilters(_a) {
    var navigateTo = _a.navigateTo, dateFrom = _a.dateFrom, dateTo = _a.dateTo, userId = _a.userId, orgUnitId = _a.orgUnitId, _b = _a.filters, filters = _b === void 0 ? {} : _b;
    navigateToChartDestination(toSearchMeetingsDestination(filters), {
        navigateTo: navigateTo,
        dateFrom: dateFrom,
        dateTo: dateTo,
        userId: userId,
        orgUnitId: orgUnitId
    });
}
export function useMeetAppsSearchNavigation() {
    var _a = useFilters(), dateFrom = _a.dateFrom, dateTo = _a.dateTo, orgUnit = _a.orgUnit, user = _a.user;
    var navigateTo = useNavigateTo();
    return useCallback(function (filters) {
        if (filters === void 0) { filters = {}; }
        navigateToSearchMeetingsWithFilters({
            navigateTo: navigateTo,
            dateFrom: dateFrom,
            dateTo: dateTo,
            userId: user,
            orgUnitId: orgUnit,
            filters: filters
        });
    }, [dateFrom, dateTo, navigateTo, orgUnit, user]);
}
export function getMeetSearchFiltersForScheduleKey(key) {
    switch (key) {
        case 'recurring':
            return { meetingType: MeetingType.RECURRING };
        case 'adhoc':
            return { meetingType: MeetingType.NON_SCHEDULED };
        case 'scheduled':
            return { meetingType: MeetingType.SCHEDULED };
        default:
            return {};
    }
}
export function getMeetSearchFiltersForLengthKey(key) {
    switch (key) {
        case 'short':
            return {
                meetingDurationType: MeetingDurationType.DURATION_LESS,
                durationHours: 0,
                durationMinutes: 15
            };
        case 'medium':
            return {
                meetingDurationType: MeetingDurationType.DURATION_MORE,
                durationHours: 0,
                durationMinutes: 15
            };
        case 'long':
            return {
                meetingDurationType: MeetingDurationType.DURATION_MORE,
                durationHours: 0,
                durationMinutes: 30
            };
        case 'veryLong':
            return {
                meetingDurationType: MeetingDurationType.DURATION_MORE,
                durationHours: 0,
                durationMinutes: 60
            };
        default:
            return {};
    }
}
export function getMeetSearchFiltersForPlannedVsActualKey(key) {
    switch (key) {
        case 'overrun':
            return {
                meetingDurationType: MeetingDurationType.OVERRUN_DURATION_MORE,
                durationHours: 0,
                durationMinutes: PLANNED_VS_ACTUAL_THRESHOLD_MINUTES
            };
        case 'underrun':
            return {
                meetingDurationType: MeetingDurationType.OVERRUN_DURATION_LESS,
                durationHours: 0,
                durationMinutes: PLANNED_VS_ACTUAL_THRESHOLD_MINUTES
            };
        case 'onTime':
            return {
                meetingDurationType: MeetingDurationType.ON_TIME_WITHIN,
                durationHours: 0,
                durationMinutes: PLANNED_VS_ACTUAL_THRESHOLD_MINUTES
            };
        default:
            return {};
    }
}
export function getMeetSearchFiltersForWhoAttendedKey(key) {
    switch (key) {
        case 'withExternal':
        case 'externalHeavy':
            return {
                meetingParticipantsType: MeetingParticipantsType.WITH_EXTERNAL_PARTICIPANTS
            };
        case 'teamInternal':
        case 'crossTeam':
            return {
                meetingParticipantsType: MeetingParticipantsType.WITH_INTERNAL_PARTICIPANTS
            };
        default:
            return {};
    }
}
export function getMeetSearchFiltersForMeetingsTypeTitle(titleKey) {
    switch (titleKey) {
        case 'app.apps.meeting.meetingsTypes.companyInternal':
            return {
                meetingParticipantsType: MeetingParticipantsType.WITH_INTERNAL_PARTICIPANTS
            };
        case 'app.apps.meeting.meetingsTypes.recurring':
            return {
                meetingType: MeetingType.RECURRING,
                meetingParticipantsType: MeetingParticipantsType.WITH_INTERNAL_PARTICIPANTS
            };
        case 'app.apps.meeting.meetingsTypes.occasional':
            return {
                meetingType: MeetingType.ONE_TIME,
                meetingParticipantsType: MeetingParticipantsType.WITH_INTERNAL_PARTICIPANTS
            };
        case 'app.apps.meeting.meetingsTypes.withExternalParticipants':
            return {
                meetingParticipantsType: MeetingParticipantsType.WITH_EXTERNAL_PARTICIPANTS
            };
        case 'app.apps.meeting.meetingsTypes.total':
            return {};
        default:
            return {};
    }
}
export function getMeetSearchFiltersForMeetInsightOverrun(title) {
    return {
        meetingType: MeetingType.RECURRING,
        meetingDurationType: MeetingDurationType.OVERRUN_DURATION_MORE,
        durationHours: 0,
        durationMinutes: OVERRUN_MINUTES,
        title: title !== null && title !== void 0 ? title : null
    };
}
export function navigateToSearchMeetingsFromMeetUsageTrendBar(_a) {
    var _b, _c, _d;
    var navigateTo = _a.navigateTo, chartData = _a.chartData, dataIndex = _a.dataIndex, category = _a.category, dateFrom = _a.dateFrom, dateTo = _a.dateTo, userId = _a.userId, orgUnitId = _a.orgUnitId, orgUnits = _a.orgUnits;
    var resolvedOrgUnitId = userId
        ? null
        : ((_c = (_b = orgUnits.find(function (orgUnit) { return orgUnit.title === category; })) === null || _b === void 0 ? void 0 : _b.id) !== null && _c !== void 0 ? _c : orgUnitId);
    navigateToSearchMeetingsFromChartPeriod({
        navigateTo: navigateTo,
        periodIndex: dataIndex,
        periodLabel: (_d = chartData[dataIndex]) === null || _d === void 0 ? void 0 : _d.name,
        dateFrom: dateFrom,
        dateTo: dateTo,
        userId: userId,
        orgUnitId: resolvedOrgUnitId,
        stack: ComparisonPeriod.Current
    });
}
export function navigateFromPeriodChartBarClick(_a) {
    var chartData = _a.chartData, barData = _a.barData, fallbackIndex = _a.fallbackIndex, stack = _a.stack, _b = _a.meetingType, meetingType = _b === void 0 ? null : _b, options = __rest(_a, ["chartData", "barData", "fallbackIndex", "stack", "meetingType"]);
    navigateChartPeriodBarClick(__assign({ chartData: chartData, barData: barData, fallbackIndex: fallbackIndex, stack: stack, destination: { kind: 'searchMeetings', meetingType: meetingType } }, options));
}
export function navigateToSearchMeetingsFromChartPeriod(_a) {
    var navigateTo = _a.navigateTo, periodIndex = _a.periodIndex, periodLabel = _a.periodLabel, dateFrom = _a.dateFrom, dateTo = _a.dateTo, userId = _a.userId, orgUnitId = _a.orgUnitId, _b = _a.meetingType, meetingType = _b === void 0 ? null : _b, _c = _a.stack, stack = _c === void 0 ? ComparisonPeriod.Current : _c;
    navigateToChartDestinationFromPeriod({
        destination: { kind: 'searchMeetings', meetingType: meetingType },
        navigateTo: navigateTo,
        periodIndex: periodIndex,
        periodLabel: periodLabel,
        rangeDateFrom: dateFrom,
        rangeDateTo: dateTo,
        userId: userId,
        orgUnitId: orgUnitId,
        stack: stack
    });
}
export function navigateToSearchMeetingsForDateRange(_a) {
    var navigateTo = _a.navigateTo, dateFrom = _a.dateFrom, dateTo = _a.dateTo, userId = _a.userId, orgUnitId = _a.orgUnitId, _b = _a.meetingType, meetingType = _b === void 0 ? null : _b, _c = _a.activityLabelId, activityLabelId = _c === void 0 ? null : _c;
    navigateToChartDestination({
        kind: 'searchMeetings',
        meetingType: meetingType,
        activityLabelId: activityLabelId
    }, { navigateTo: navigateTo, dateFrom: dateFrom, dateTo: dateTo, userId: userId, orgUnitId: orgUnitId });
}
